import React from 'react';
import { getInstrumentSpec } from '../config/instruments';

interface Props {
  isOpen: boolean;
  symbol: string;
  action: 'Buy' | 'Sell';
  quantity: number;
  onConfirm: () => void;
  onCancel: () => void;
}

const MICRO_MAP: Record<string, string> = {
  NQ: 'MNQ', ES: 'MES', YM: 'MYM', RTY: 'M2K',
};

const FullSizeContractConfirmModal: React.FC<Props> = ({
  isOpen, symbol, action, quantity, onConfirm, onCancel,
}) => {
  if (!isOpen) return null;

  const spec = getInstrumentSpec(symbol);
  const microSymbol = MICRO_MAP[spec.symbol] || 'Micro';
  const microSpec = getInstrumentSpec(microSymbol);
  const multiplier = Math.round(spec.pointValue / microSpec.pointValue);

  return (
    <div style={styles.backdrop} onClick={onCancel}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.headerIcon}>🔴</span>
          <span style={styles.headerTitle}>Full-Size Contract</span>
        </div>

        {/* Body */}
        <div style={styles.body}>
          <p style={styles.subtitle}>
            You are about to <strong style={{ color: action === 'Buy' ? '#26a69a' : '#ef5350' }}>{action}</strong> {quantity}x <strong style={{ color: '#ef4444' }}>{spec.name}</strong>
          </p>

          <div style={styles.comparisonBox}>
            <div style={styles.compRow}>
              <span style={styles.compLabel}>Contract</span>
              <span style={{ ...styles.compValue, color: '#ef4444' }}>{spec.symbol}</span>
              <span style={styles.compVs}>vs</span>
              <span style={{ ...styles.compValue, color: '#26a69a' }}>{microSymbol}</span>
            </div>
            <div style={styles.compRow}>
              <span style={styles.compLabel}>Per Tick</span>
              <span style={{ ...styles.compValue, color: '#ef4444' }}>${spec.tickValue.toFixed(2)}</span>
              <span style={styles.compVs}>vs</span>
              <span style={{ ...styles.compValue, color: '#26a69a' }}>${microSpec.tickValue.toFixed(2)}</span>
            </div>
            <div style={styles.compRow}>
              <span style={styles.compLabel}>Per Point</span>
              <span style={{ ...styles.compValue, color: '#ef4444' }}>${spec.pointValue}</span>
              <span style={styles.compVs}>vs</span>
              <span style={{ ...styles.compValue, color: '#26a69a' }}>${microSpec.pointValue}</span>
            </div>
            <div style={styles.compRow}>
              <span style={styles.compLabel}>Multiplier</span>
              <span style={{ ...styles.compValue, color: '#ef4444', fontSize: 14, fontWeight: 800 }}>{multiplier}x</span>
              <span style={styles.compVs}></span>
              <span style={styles.compValue}></span>
            </div>
          </div>

          <p style={styles.disclaimer}>
            Full-size contracts carry {multiplier}x the risk of {microSymbol}. A 10-point move = ${spec.pointValue * 10 * quantity} (vs ${microSpec.pointValue * 10 * quantity} on {microSymbol}).
          </p>
        </div>

        {/* Actions */}
        <div style={styles.actions}>
          <button style={styles.cancelBtn} onClick={onCancel}>
            Cancel
          </button>
          <button style={styles.confirmBtn} onClick={onConfirm}>
            Accept Risk & {action}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(8px)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: 'fadeIn 0.15s ease',
  },
  modal: {
    background: 'linear-gradient(135deg, rgba(15, 20, 40, 0.98) 0%, rgba(40, 15, 15, 0.95) 100%)',
    backdropFilter: 'blur(24px)',
    border: '1px solid rgba(239, 68, 68, 0.35)',
    borderRadius: 16,
    boxShadow: '0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06) inset, 0 0 60px rgba(239,68,68,0.1)',
    width: 360,
    maxWidth: '90vw',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '14px 18px',
    borderBottom: '1px solid rgba(239, 68, 68, 0.2)',
  },
  headerIcon: {
    fontSize: 18,
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#ef4444',
    letterSpacing: '0.02em',
  },
  body: {
    padding: '14px 18px',
  },
  subtitle: {
    fontSize: 13,
    color: '#d1d5db',
    margin: '0 0 12px 0',
    lineHeight: 1.5,
  },
  comparisonBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginBottom: 12,
    padding: '10px 12px',
    background: 'rgba(239, 68, 68, 0.06)',
    borderRadius: 10,
    border: '1px solid rgba(239, 68, 68, 0.15)',
  },
  compRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  compLabel: {
    fontSize: 11,
    color: '#9ca3af',
    width: 65,
    flexShrink: 0,
  },
  compValue: {
    fontSize: 12,
    fontWeight: 600,
    flex: 1,
    textAlign: 'center' as const,
  },
  compVs: {
    fontSize: 10,
    color: '#6b7280',
    width: 20,
    textAlign: 'center' as const,
  },
  disclaimer: {
    fontSize: 11,
    color: '#f87171',
    margin: 0,
    lineHeight: 1.4,
    fontWeight: 500,
  },
  actions: {
    display: 'flex',
    gap: 8,
    padding: '12px 18px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    justifyContent: 'flex-end',
  },
  cancelBtn: {
    padding: '8px 16px',
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.06)',
    color: '#d1d5db',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
  },
  confirmBtn: {
    padding: '8px 16px',
    borderRadius: 8,
    border: '1px solid rgba(239, 68, 68, 0.4)',
    background: 'rgba(239, 68, 68, 0.15)',
    color: '#ef4444',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
};

export default FullSizeContractConfirmModal;
